/**
 * Tiny global loading-state tracker — components/handlers push a label while
 * a heavy operation runs and pop it when done. The viewport renders the most
 * recent label as a centred overlay. Stackable so concurrent calls don't fight
 * each other to clear the indicator.
 */

type Listener = (active: string[]) => void;

let active: string[] = [];
const listeners = new Set<Listener>();

function emit() {
  const snapshot = active.slice();
  for (const l of listeners) l(snapshot);
}

let nextId = 1;

export function begin(label: string): () => void {
  const id = `${nextId++}:${label}`;
  active = [...active, id];
  emit();
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    active = active.filter((x) => x !== id);
    emit();
  };
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(active.slice());
  return () => { listeners.delete(listener); };
}

/** Get the label portion of the most-recent active entry, for display. */
export function currentLabel(): string | null {
  if (active.length === 0) return null;
  const top = active[active.length - 1];
  return top.slice(top.indexOf(":") + 1);
}
